import { useCallback, useRef } from 'react';
import { useAppDispatch, useAppStore } from 'store/hooks';
import { LoadingAppViewModal } from 'design-system/LoadingAppView';
import QuotesModal from 'features/feed/components/save/components/QuotesModal';
import { getEnquiries } from 'actions';
import { GET_ENQUIRIES_SUCCESS } from 'actions/types';
import { selectFeatureFlag, selectIsGBUser } from 'reducers/selectors';
import Links from 'utils/links';
import Sentry from 'utils/sentry';
import { isCreditCardType, monevoPreviousQuoteAgeDays, resolveMonevoEntry, } from '../entry';
import { loanTypeReasonValue } from '../models';
import { startMonevoOnboarding, } from '../reducers/monevoOnboarding';
import { submitMonevoQuote } from '../submit';
import useMonevoNavigation from './useMonevoNavigation';
const MONEVO_PARTNER = 'MONEVO_UK';
const MONEVO_WEB_URL = 'https://emma.monevo.co.uk/';
const strings = {
    loading: 'Getting your quotes',
};
/**
 * The single owner of what a loan or credit-card tap does. Every entry point
 * in the app goes through here, which is what puts them all under the same
 * country check, feature flag and enquiry lookup — several of them used to
 * start the funnel directly and so were under none of the three.
 */
const useMonevoEntry = () => {
    const store = useAppStore();
    const dispatch = useAppDispatch();
    const { startMonevoFlow } = useMonevoNavigation();
    /**
     * A credit-card tap submits a real credit search, so a second tap while the
     * first is in flight must do nothing. Held from the enquiry lookup until
     * the quote request settles.
     */
    const isResolving = useRef(false);
    /**
     * Asks for a credit-card quote with no screen in between: there is nothing
     * left to collect, so the submission happens behind a full-screen,
     * non-dismissable loader. Consent is the one given with the previous quote.
     */
    const submitCreditCardQuote = useCallback(async (type, details) => {
        dispatch(startMonevoOnboarding({
            type,
            loanPurpose: loanTypeReasonValue.CREDIT_CARD,
            ...details,
        }));
        LoadingAppViewModal.show({ title: strings.loading });
        let outcome = { status: 'error' };
        try {
            outcome = await submitMonevoQuote(dispatch, store.getState);
        }
        finally {
            LoadingAppViewModal.hide(() => {
                if (outcome.status === 'success') {
                    Links.openLink(outcome.url);
                    return;
                }
                /**
                 * A saved detail is missing or no longer usable. The funnel is what
                 * collects those, so the user walks it with the reuse flag cleared.
                 */
                if (outcome.status === 'needsFunnel') {
                    startMonevoFlow(type, { reuseDetails: false });
                }
            });
        }
    }, [startMonevoFlow]);
    const resolveEntry = useCallback(async (enquiries, type, skipQuotesModal) => {
        const entry = resolveMonevoEntry(enquiries, type, { skipQuotesModal });
        if (entry.kind === 'quotesModal') {
            /**
             * "Start a new quote" has already been offered the live quote, so it
             * skips this branch — but it still evaluates reuse, because a live
             * enquiry is not proof the saved data is still usable.
             */
            QuotesModal.show(entry.url, () => resolveEntry(enquiries, type, true));
            return;
        }
        if (entry.kind === 'funnel') {
            startMonevoFlow(type);
            return;
        }
        const details = {
            ...entry.prefill,
            reuseDetails: true,
            previousQuoteAgeDays: monevoPreviousQuoteAgeDays(enquiries),
        };
        if (isCreditCardType(type)) {
            await submitCreditCardQuote(type, details);
            return;
        }
        /**
         * A loan still needs an amount, a term and a reason, so the loan amount
         * screen is shown — prefilled, and submitting on press.
         */
        startMonevoFlow(type, details);
    }, [startMonevoFlow, submitCreditCardQuote]);
    return useCallback(async (type) => {
        const state = store.getState();
        if (!selectIsGBUser(state))
            return;
        if (!selectFeatureFlag(state, 'monevo_api').value) {
            Links.openLink(MONEVO_WEB_URL);
            return;
        }
        if (isResolving.current)
            return;
        isResolving.current = true;
        try {
            const result = await dispatch(getEnquiries(MONEVO_PARTNER, true));
            const enquiries = result?.type === GET_ENQUIRIES_SUCCESS ? result.payload ?? [] : [];
            await resolveEntry(enquiries, type);
        }
        catch (error) {
            Sentry.captureException(error);
        }
        finally {
            isResolving.current = false;
        }
    }, [resolveEntry]);
};
export default useMonevoEntry;
